import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Property, SelectField } from '@/components/form/fields/select/SelectInput';
import { NumberField } from '@/components/form/fields/text/NumberInput';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import {
  useCreateCustomerCreditCheck,
  useCreditCheckerCustomer,
  useCustomerCreditCheck
} from '@/services/api/hooks/credit';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { CreditCheckStatus, getCreditCheckStatus, getCreditStatusOptions } from '../creditCheckHelper';
import { useCurrencyIdentifier } from '@/utils/price';
import { useMspDetailsForCustomer } from '@/services/api/hooks/companies';

const EditCreditCheckFormFields = z.object({
  status: z.custom<Property>(),
  limit: z.number()
});

type EditCreditCheckFormFields = z.infer<typeof EditCreditCheckFormFields>;

const EditCreditCheckModal: FunctionComponent<
  ModalProps<{ mspId: string; customerId: string; creditCheckId?: string; regionCode: string; }>
> = props => {
  const EditCreditCheck = () => {
    const mspId = props.props?.mspId;
    const customerId = props.props?.customerId;
    if (!(mspId && customerId)) throw new Error('Missing query param');

    const [customer] = useCreditCheckerCustomer(customerId);

    const [creditCheck] = useCustomerCreditCheck(mspId, customerId, props.props?.creditCheckId);

    const createCreditCheck = useCreateCustomerCreditCheck();

    const currencyIdentifier = useCurrencyIdentifier(props.props?.regionCode, false);

    const methods = useForm<EditCreditCheckFormFields>({
      resolver: zodResolver(EditCreditCheckFormFields),
      defaultValues: {
        limit: (creditCheck && creditCheck?.creditLimit) ?? 0,
        ...(creditCheck
          ? {
              status: {
                id: String(creditCheck.status),
                name: getCreditCheckStatus(creditCheck.status)
              }
            }
          : {})
      }
    });

    const onSubmit = async (data: EditCreditCheckFormFields) => {
      await createCreditCheck.mutateAsync({
        mspId,
        customerId,
        creditLimit: data.limit,
        status: data.status.id as unknown as CreditCheckStatus
      });
      props.close();
    };

    const errorObj = createCreditCheck.error as Error | undefined;
    const error = errorObj ? errorObj.message : '';

    return (
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Edit Customer Credit</h1>
        </Modal.Controls>
        <Form className="flex w-full flex-col space-y-4 py-3" methods={methods} onSubmit={onSubmit}>
          <div className="flex-col space-y-1">
            <p>Name</p>
            <p className="font-semibold">{customer.name}</p>
          </div>

          <SelectField id="status" label="Status" placeholder="Please select" options={getCreditStatusOptions()} />

          <NumberField id="limit" label={`Credit Limit (${currencyIdentifier})`} />

          {error && <p className="text-red-500">{error}</p>}

          <div className="flex justify-end pt-2">
            <Button
              loading={createCreditCheck.isLoading}
              className="w-fit"
              disabled={methods.getValues('status') === undefined}
              variant={ButtonVariant.Primary}
              type="submit"
            >
              Submit
            </Button>
          </div>
        </Form>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <EditCreditCheck />
      </Suspense>
    </Modal>
  );
};

export default EditCreditCheckModal;
